import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function getScoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '28px',
    border: '1px solid #e5e7eb', marginBottom: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  overallScore: { textAlign: 'center', padding: '16px 0' },
  scoreValue: { fontSize: '72px', fontWeight: '800', lineHeight: '1' },
  scoreLabel: { fontSize: '16px', color: '#6b7280', marginTop: '8px' },
  scoreBar: {
    width: '100%', height: '8px', backgroundColor: '#e5e7eb',
    borderRadius: '4px', overflow: 'hidden', marginTop: '16px',
  },
  scoreBarFill: { height: '100%', borderRadius: '4px', transition: 'width 0.8s ease' },
  metricRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid #f3f4f6',
  },
  metricLabel: { fontSize: '14px', color: '#374151', fontWeight: '500' },
  metricScore: {
    fontSize: '14px', fontWeight: '700', padding: '3px 10px',
    borderRadius: '20px', minWidth: '50px', textAlign: 'center',
  },
  summary: { fontSize: '15px', color: '#374151', lineHeight: '1.7' },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  listItem: { display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', color: '#374151', lineHeight: '1.6' },
  strengthDot: { color: '#16a34a', fontWeight: '700', flexShrink: 0, fontSize: '16px' },
  improveDot: { color: '#d97706', fontWeight: '700', flexShrink: 0, fontSize: '16px' },
  keyMoment: {
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
    marginBottom: '8px', borderLeft: '3px solid',
  },
  btnRow: { display: 'flex', gap: '12px' },
  practiceAgainBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '12px 24px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  backBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '12px 24px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  loading: { textAlign: 'center', padding: '64px', color: '#6b7280', fontSize: '15px' },
};

export default function PracticeReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [turns, setTurns] = useState([]);

  useEffect(() => { fetchData(); }, [id]);

  async function fetchData() {
    const [sessionRes, turnsRes] = await Promise.all([
      fetch(`${API}/api/practice/sessions/${id}`),
      fetch(`${API}/api/practice/sessions/${id}/turns`),
    ]);
    setSession(await sessionRes.json());
    const turnsData = await turnsRes.json();
    setTurns(Array.isArray(turnsData) ? turnsData : []);
  }

  if (!session) return <AppLayout><div style={styles.loading}>Loading report...</div></AppLayout>;
  if (!session.evaluation) return <AppLayout><div style={styles.loading}>Evaluation not ready yet...</div></AppLayout>;

  const { evaluation } = session;

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Practice Report</div>
          <div style={styles.sub}>{session.template_title} · {turns.filter(t => t.role === 'user').length} responses</div>
          {session.goals && (
            <div style={{
              fontSize: '13px', color: '#0052CC', backgroundColor: '#EBF5FF',
              padding: '6px 12px', borderRadius: '6px', marginTop: '8px',
              display: 'inline-block', fontWeight: '500',
            }}>
              🎯 Goals: {session.goals}
            </div>
          )}
        </div>
        <div style={styles.btnRow}>
          <button style={styles.backBtn} onClick={() => navigate('/practice')}>← Practice Again</button>
          <button style={styles.practiceAgainBtn} onClick={() => navigate('/practice/history')}>View History</button>
        </div>
      </div>

      {/* Overall Score */}
      <div style={styles.card}>
        <div style={styles.overallScore}>
          <div style={{ ...styles.scoreValue, color: getScoreColor(evaluation.overall_score) }}>
            {evaluation.overall_score}
          </div>
          <div style={styles.scoreLabel}>Overall Score</div>
          <div style={styles.scoreBar}>
            <div style={{
              ...styles.scoreBarFill,
              width: `${evaluation.overall_score}%`,
              backgroundColor: getScoreColor(evaluation.overall_score),
            }} />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Summary</div>
        <p style={styles.summary}>{evaluation.summary}</p>
      </div>

      {/* Metric Breakdown */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Performance Breakdown</div>
        {Object.entries(evaluation.scores).map(([key, score]) => (
          <div key={key} style={styles.metricRow}>
            <div style={styles.metricLabel}>
              {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </div>
            <div style={{
              ...styles.metricScore,
              backgroundColor: `${getScoreColor(score)}15`,
              color: getScoreColor(score),
            }}>
              {score}/100
            </div>
          </div>
        ))}
      </div>

      {/* Strengths & Improvements */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Strengths</div>
          <div style={styles.list}>
            {evaluation.strengths?.map((s, i) => (
              <div key={i} style={styles.listItem}>
                <span style={styles.strengthDot}>✓</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Areas to Improve</div>
          <div style={styles.list}>
            {evaluation.improvements?.map((s, i) => (
              <div key={i} style={styles.listItem}>
                <span style={styles.improveDot}>→</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Moments */}
      {evaluation.key_moments?.length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Key Moments</div>
          {evaluation.key_moments.map((m, i) => (
            <div key={i} style={{
              ...styles.keyMoment,
              backgroundColor: m.type === 'strength' ? '#f0fdf4' : '#fffbeb',
              borderLeftColor: m.type === 'strength' ? '#16a34a' : '#d97706',
              color: m.type === 'strength' ? '#15803d' : '#92400e',
            }}>
              <strong>Turn {m.turn}:</strong> {m.note}
            </div>
          ))}
        </div>
      )}

      {/* Transcript */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Full Transcript</div>
        {turns.map((turn, idx) => (
          <div key={idx} style={{
            padding: '10px 14px', marginBottom: '8px', borderRadius: '8px',
            backgroundColor: turn.role === 'ai' ? '#f9fafb' : '#EBF5FF',
          }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: turn.role === 'ai' ? '#6b7280' : '#0052CC', marginBottom: '4px', letterSpacing: '0.5px' }}>
              {turn.role === 'ai' ? 'AI' : 'YOU'}
            </div>
            <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>{turn.content}</div>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
